import { Box, IconButton, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import { colors } from '../../core/theme/theme';
import type { EntrySortDir, EntrySortKey } from './entryFilter';
import { entryGridTemplate } from './entryGrid';

/** Column keys that can be filtered from their header icon. */
export type ColumnFilterKey = 'designation' | 'teammate' | 'billable';

export interface TimeEntryHeaderProps {
  showUser: boolean;
  /**
   * When `false`, the Project column is skipped. Matches the same
   * prop on TimeEntryRow — both must agree on the column set.
   */
  showProject?: boolean;
  sortKey: EntrySortKey;
  sortDir: EntrySortDir;
  /**
   * Fires when the user clicks a sortable column label. If the clicked
   * column is already active, the parent should toggle `sortDir`;
   * otherwise switch `sortKey` and reset direction to `desc`.
   *
   * When omitted, sorting is disabled (static legend mode — used by
   * the tracker's "Today" list where 5-10 rows don't need sorting).
   */
  onSortChange?: (key: EntrySortKey) => void;
  /**
   * Fires when the user clicks the filter icon next to a column label.
   * The parent is responsible for displaying a Popover anchored on the
   * received element and rendering the right filter UI.
   *
   * When omitted, filter icons are not rendered.
   */
  onFilterClick?: (key: ColumnFilterKey, anchor: HTMLElement) => void;
  /**
   * Map of filter keys → whether a filter is currently active on the
   * column. Active filters render the icon in the primary color +
   * bold, signalling the column is narrowed.
   */
  activeFilters?: Partial<Record<ColumnFilterKey, boolean>>;
}

interface Col {
  /** i18n key for the label. */
  labelKey: string;
  /** `null` → not sortable (Actions). */
  sortKey: EntrySortKey | null;
  /** `null` → not filterable (When, Duration, Project, Description). */
  filterKey: ColumnFilterKey | null;
  testId: string;
  /** Used to skip the Teammate column when not shown. */
  userOnly?: boolean;
  /** Used to skip the Project column in per-project contexts. */
  projectOnly?: boolean;
  /** Right-align (Actions cell). */
  alignEnd?: boolean;
}

// Keep the column list aligned with `ENTRY_GRID_TEMPLATE`. Order is:
// When, Duration, Project, Designation, [Teammate], Description,
// Actions. The `filterKey` marks which columns expose an inline
// filter icon — chosen by whether the data on that column is discrete
// (multi-select) or boolean, matching the ColumnFilters shape.
const COLUMNS: readonly Col[] = [
  {
    labelKey: 'kronos.entries.whenColumn',
    sortKey: 'startTime',
    filterKey: null,
    testId: 'when',
  },
  {
    labelKey: 'kronos.entries.durationColumn',
    sortKey: 'duration',
    filterKey: null,
    testId: 'duration',
  },
  {
    labelKey: 'kronos.entries.projectColumn',
    sortKey: 'project',
    filterKey: null,
    testId: 'project',
    projectOnly: true,
  },
  {
    labelKey: 'kronos.entries.designationColumn',
    sortKey: 'designation',
    filterKey: 'designation',
    testId: 'designation',
  },
  {
    labelKey: 'kronos.entries.teammate',
    sortKey: 'teammate',
    filterKey: 'teammate',
    testId: 'teammate',
    userOnly: true,
  },
  {
    labelKey: 'kronos.entries.descriptionColumn',
    sortKey: 'description',
    filterKey: null,
    testId: 'description',
  },
  {
    labelKey: 'kronos.entries.actionsColumn',
    // Billable filter lives in the Actions column (same cell where the
    // billable chip is rendered). Keeps the affordance close to the
    // data being filtered.
    sortKey: null,
    filterKey: 'billable',
    testId: 'actions',
    alignEnd: true,
  },
];

/**
 * Column header row above the entry list.
 *
 *   • Clicking the label (sortable column) toggles the active sort.
 *   • Clicking the filter icon (filterable column) opens the parent's
 *     Popover anchored on the icon.
 *
 * The two affordances are independent: a column can be sortable AND
 * filterable (Designation, Teammate), sortable only (When, Duration,
 * Project, Description), or filterable only (Actions → Billable).
 */
export default function TimeEntryHeader({
  showUser,
  showProject = true,
  sortKey,
  sortDir,
  onSortChange,
  onFilterClick,
  activeFilters,
}: TimeEntryHeaderProps) {
  const { t } = useTranslation();
  const cols = COLUMNS.filter((c) => {
    if (c.userOnly && !showUser) return false;
    if (c.projectOnly && !showProject) return false;
    return true;
  });
  return (
    <Box
      data-testid="time-entry-header"
      sx={{
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: entryGridTemplate({ showProject, showUser }),
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        backgroundColor: colors.lightGray,
        borderTop: `1px solid ${colors.lightGray}`,
      }}
    >
      {cols.map((col) => {
        const sortActive = col.sortKey !== null && sortKey === col.sortKey;
        const sortable = col.sortKey !== null && onSortChange !== undefined;
        const filterable = col.filterKey !== null && onFilterClick !== undefined;
        const filterActive = filterable ? !!activeFilters?.[col.filterKey!] : false;
        const fireSort = () => onSortChange?.(col.sortKey as EntrySortKey);
        return (
          <Box
            key={col.testId}
            data-testid={`time-entry-header-${col.testId}`}
            onClick={sortable ? fireSort : undefined}
            role={sortable ? 'button' : undefined}
            tabIndex={sortable ? 0 : undefined}
            onKeyDown={
              sortable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fireSort();
                    }
                  }
                : undefined
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              justifyContent: col.alignEnd ? 'flex-end' : 'flex-start',
              cursor: sortable ? 'pointer' : 'default',
              userSelect: 'none',
              '&:hover': sortable ? { opacity: 0.75 } : undefined,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontWeight: sortActive ? 700 : 600,
                color: sortActive ? 'text.primary' : 'text.secondary',
              }}
            >
              {t(col.labelKey)}
            </Typography>
            {sortActive &&
              (sortDir === 'asc' ? (
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
              ))}
            {filterable && (
              <IconButton
                size="small"
                onClick={(e) => {
                  // Prevent the outer header's sort click when the user
                  // only wanted to open the filter menu.
                  e.stopPropagation();
                  onFilterClick!(col.filterKey as ColumnFilterKey, e.currentTarget);
                }}
                aria-label={t('kronos.projectTab.filter')}
                data-testid={`time-entry-header-filter-${col.filterKey}`}
                sx={{
                  p: 0.25,
                  ml: 0.25,
                  color: filterActive ? 'primary.main' : 'text.secondary',
                }}
              >
                <FilterListIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
